import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EyeOff, Eye } from "lucide-react";
import toast from "react-hot-toast";
import {
  UserLogInAction,
  clearLoginStates,
  clearPendingEmailForOTP,
  clearPendingEmailForPasswordOtp,
  clearPendingOtpForPasswordReset,
} from "../../Store/Actions/AuthActions";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

const FormSection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state - ONLY for loader (UI state)
  // Login page doesn't need OTP-related states - they should all be cleared
  const { loader } = useSelector((state) => state.auth);
  
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckEmail, setIsCheckEmail] = useState(false);
  const [isCheckPassword, setIsCheckPassword] = useState(false);
  const [email, setemail] = useState("");
  const [password, setpassword] = useState("");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Email validation regex
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Clear all login-related and OTP-related states when component mounts
  // This ensures no stale state persists from previous flows
  useEffect(() => {
    dispatch(clearLoginStates());
    dispatch(clearPendingEmailForOTP()); // This clears otpSuccess, otpError, otpLoading, and pendingEmailForOTP
    dispatch(clearPendingEmailForPasswordOtp());
    dispatch(clearPendingOtpForPasswordReset());
  }, [dispatch]);

  const fetchRejectionReason = async (email) => {
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_REJECTION_REASON}?email=${encodeURIComponent(email)}`
      );
      const result = await response.json();
      
      if (result.statusCode === 200 && result.data?.activeRejection) {
        const rejection = result.data.activeRejection;
        // Format the rejection reason for display
        let rejectionMessage = "Account rejected";
        
        if (rejection.reasonType === "OTHER" && rejection.detail) {
          rejectionMessage = `Account rejected: ${rejection.detail}`;
        } else {
          const reasonMap = {
            MISSING_DETAILS: "Missing Details",
            EXPIRED_DOCUMENTS: "Expired Documents",
            RESTRICT_ACCOUNT: "Restrict Account",
            OTHER: "Other reason",
          };
          rejectionMessage = `Account rejected: ${reasonMap[rejection.reasonType] || rejection.reasonType}`;
        }
        
        // Navigate to account approval page with rejection reason
        navigate("/account-approval", {
          state: { rejectionReason: rejectionMessage, rejectionData: rejection },
        });
      } else {
        // If API fails, still navigate but with generic message
        navigate("/account-approval", {
          state: { rejectionReason: "Account rejected" },
        });
      }
    } catch (error) {
      console.error("Error fetching rejection reason:", error);
      // If API fails, still navigate but with generic message
      navigate("/account-approval", {
        state: { rejectionReason: "Account rejected" },
      });
    }
  };

  const proceedhandler = async (e) => {
    e.preventDefault();
    
    // Reset error states
    setIsCheckEmail(false);
    setIsCheckPassword(false);

    // Track errors to show all at once
    let hasError = false;
    let errorCount = 0;
    let hasEmailError = false;

    // Validate email (required + regex)
    if (!email || email.trim() === "") {
      setIsCheckEmail(true);
      errorCount++;
      hasEmailError = true;
      hasError = true;
    } else if (!validateEmail(email)) {
      setIsCheckEmail(true);
      errorCount++;
      hasEmailError = true;
      hasError = true;
    }

    // Validate password (required)
    if (!password || password.trim() === "") {
      setIsCheckPassword(true);
      errorCount++;
      hasError = true;
    }

    // If there are errors, show toast and return
    if (hasError) {
      if (errorCount > 1) {
        toast("Please fill out all required fields", { icon: '⚠️' });
      } else if (hasEmailError) {
        toast("Please enter a valid email", { icon: '⚠️' });
      } else {
        toast("Please enter your password", { icon: '⚠️' });
      }
      return;
    }

    // Define callbacks for handling login response
    const handleLoginSuccess = (result) => {
      toast.success("Login successful!");
      setTimeout(() => {
        navigate("/dashboard");
      }, 1000);
    };

    const handleLoginError = (error, sanitizedEmail, hasEmailPending) => {
      const messagesRaw = Array.isArray(error.message)
        ? error.message
        : error.message
        ? [error.message]
        : [];

      const messages = messagesRaw.filter((msg) => typeof msg === "string");
      const normalizedMessages = messages.map((msg) => msg.toLowerCase());

      // Check for blocked account - fetch block reason from API (same rejection endpoint)
      const hasAccountBlocked = normalizedMessages.some((msg) =>
        msg.includes("account blocked by administrator")
      );

      if (hasAccountBlocked) {
        fetchRejectionReason(sanitizedEmail);
        return;
      }

      // Check for rejected account FIRST - fetch rejection reason from API
      const hasAccountRejected = normalizedMessages.some((msg) =>
        msg.includes("account rejected")
      );

      if (hasAccountRejected) {
        // Fetch rejection reason from API
        fetchRejectionReason(sanitizedEmail);
        return;
      }

      const hasAccountPending = normalizedMessages.some((msg) =>
        msg.includes("account pending approval")
      );

      if (hasEmailPending) {
        toast("Please verify your email to continue.", { icon: "ℹ️" });
        navigate("/otp");
        return;
      }

      if (hasAccountPending) {
        toast("Your account is pending approval.", { icon: "ℹ️" });
        navigate("/account-approval");
        return;
      }

      const errorMessage =
        messages.join(", ") ||
        error.error ||
        "Login failed. Please try again.";
      toast.error(errorMessage);
    };

    // Dispatch login action with callbacks
    dispatch(
      UserLogInAction(
        { email, password },
        handleLoginSuccess,
        handleLoginError
      )
    );
  };

  return (
    <form className="w-full mt-8" onSubmit={proceedhandler}>
      <div class="relative w-full">
        <input
          type="text"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setemail(e.target.value);
            if (isCheckEmail) setIsCheckEmail(false);
          }}
          class={`peer w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckEmail
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
      </div>
      <div class="relative w-full mt-3">
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          value={password}
          onChange={(e) => {
            setpassword(e.target.value);
            if (isCheckPassword) setIsCheckPassword(false);
          }}
          class={`w-full font-popinsMedium rounded-xl border-2 bg-white px-4 py-2 !text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckPassword
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
        <div
          title="Show password"
          className="absolute right-3 top-3 cursor-pointer text-black/50"
          onClick={togglePasswordVisibility}
        >
          {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
        </div>
      </div>
      <div className="text-left mt-3">
        <Link
          to={"/forget-password"}
          className=" text-themeColor font-popinsMedium "
        >
          <p>Forget Password</p>
        </Link>
      </div>
      <button
        type="submit"
        disabled={loader}
        className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-lg mt-8 rounded-md shadow-lg
           transform transition duration-300 ease-in-out
           hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
           active:scale-95 active:shadow-inner focus:outline-none w-full
           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        <span className="flex items-center justify-center">
          {loader ? (
            <>
              <svg
                aria-hidden="true"
                class="w-6 h-6 text-gray-200 animate-spin dark:text-white fill-indigo-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span class="ml-2">Logging in...</span>
            </>
          ) : (
            "Log in"
          )}
        </span>
        <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
      </button>
      <div className="flex items-center justify-end mt-3 text-base">
        <p className="font-popinsMedium">Do you have account?</p>
        <Link to={"/signup"}>
          <p className="font-popinsMedium text-themeColor pl-2">Sign up</p>
        </Link>
      </div>
    </form>
  );
};

export default FormSection;
